import React, { useState, useEffect, Fragment } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import { useTranslation } from "react-i18next";
import Slide from "@mui/material/Slide";
import { Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import FullScreenIcon from "@mui/icons-material/FullscreenOutlined";
import FullScreenExitIcon from "@mui/icons-material/FullscreenExitOutlined";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import makeStyles from "@mui/styles/makeStyles";
import BackIcon from "@mui/icons-material/ArrowBack";
import { IsMobile } from "../utils/utils";
import { confirmAlert } from "../utils/alerts";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const useStyles = makeStyles((theme) => ({
  appBar: {
    position: "relative",
  },
  title: {
    marginLeft: theme.spacing(2),
    color: "#fff",
    flex: 1,
  },
  mobileTitle: {
    marginLeft: 0,
    color: "#fff",
    flex: 1,
    fontSize: "1.1rem",
  },
  root: {
    backgroundColor: "transparent",
  },

  paper: {
    backgroundColor: "white",
    // boxShadow: "none",
    overflow: "hidden",
  },
  paperGray: {
    backgroundColor: "#eeeeee",
    // boxShadow: "none",
    overflow: "hidden",
  },
}));

const AlertDialog = (props) => {
  const [open, setOpen] = useState(props.show);
  const { t } = useTranslation();
  const classes = useStyles();
  const isMobile = IsMobile();
  const [fullScreen, setFullScreen] = useState(props.fullScreen || (isMobile && props.fullScreenMobile));
  useEffect(() => {
    setOpen(props.show);
  }, [props.show]);

  function handleClose() {
    // setOpen(false);
    if (props.onClose) {
      if (props.checkBeforeClose && props.item && props.origItemRef) {
        if (props.item.isDifferent(props.origItemRef.current)) {
          confirmAlert({
            t: t,
            title: t("attention"),
            description: t("advices.unsavedChanges"),
            confirmText: t("actions.exitWithoutSaving"),
            onConfirm: props.onClose,
          });
        } else {
          props.onClose();
        }
      } else {
        props.onClose();
      }
    }
  }
  function handleAccept() {
    if (props.onAccept) props.onAccept();
  }

  const fullScreenHandler = () => {
    setFullScreen(!fullScreen);
  };

  useEffect(() => {
    if (props.onFullScreenChange) {
      props.onFullScreenChange(fullScreen);
    }
  }, [fullScreen]);

  const getDialogContent = () => {
    return (
      <Fragment>
        {(fullScreen || props.appbarTitle) && (
          <AppBar className={classes.appBar}>
            <Toolbar className={isMobile ? "pr-1 pl-3" : ""}>
              {props.onBack && (
                <IconButton
                  edge="start"
                  className={isMobile ? "mr-1" : "mr-2"}
                  onClick={handleClose}
                  color="inherit"
                  aria-label="menu"
                  size="large"
                >
                  <BackIcon />
                </IconButton>
              )}
              {props.title && (
                <Typography variant={"h6"} className={isMobile ? classes.mobileTitle : classes.title}>
                  {props.title}
                </Typography>
              )}
              {props.rightTitle && <div>{props.rightTitle}</div>}
              {!props.fullScreen && props.fullScreenOp && (
                <IconButton
                  edge="start"
                  color="inherit"
                  onClick={fullScreenHandler}
                  aria-label="fullscreen"
                  className="ml-2"
                  size="large"
                >
                  {!fullScreen ? <FullScreenIcon /> : <FullScreenExitIcon />}
                </IconButton>
              )}
              {props.onClose &&
                (props.onCloseElement || (
                  <IconButton
                    edge="start"
                    color="inherit"
                    onClick={handleClose}
                    aria-label="close"
                    className="ml-2"
                    size="large"
                  >
                    <CloseIcon />
                  </IconButton>
                ))}
            </Toolbar>
          </AppBar>
        )}
        {props.title && !fullScreen && !props.appbarTitle && (
          <div id="alert-dialog-title" className="d-flex align-items-center justify-content-between w-100 p-3">
            <Typography variant="h6">{props.title}</Typography>
            {props.rightTitle && <div>{props.rightTitle}</div>}

            <div className="d-flex align-items-center">
              {props.fullScreenOp && (
                <IconButton
                  edge="start"
                  color="inherit"
                  onClick={fullScreenHandler}
                  aria-label="fullscreen"
                  className="ml-2 mr-2"
                  size="large"
                >
                  {!fullScreen ? <FullScreenIcon /> : <FullScreenExitIcon />}
                </IconButton>
              )}

              {!props.rightTitle && props.onClose && (
                <IconButton aria-label="close" onClick={handleClose} className="p-2" size="large">
                  <CloseIcon />
                </IconButton>
              )}
            </div>
          </div>
        )}
        <DialogContent
          style={{ height: props.height || "inherit" }}
          className={[
            props.contentClassName || "",
            fullScreen || props.appbarTitle ? "p-0 d-flex flex-column flex-grow-1" : "",
            props.fullScreenMobile && isMobile ? "p-2" : "",
            props.overflowHidden ? "overflow-hidden" : "",
          ].join(" ")}
        >
          <DialogContentText id="alert-dialog-description">
            <span className="text-color white-space-pre-line">{props.description}</span>
          </DialogContentText>
          {props.children}
        </DialogContent>
        {!props.hideFooter && (
          <DialogActions style={{ borderTop: "1px solid #e9e9e9" }}>
            <div
              className={
                isMobile
                  ? "d-flex flex-column w-100 align-items-end"
                  : "d-flex align-items-center justify-content-between w-100 pl-3 pr-3 pt-1 pb-1"
              }
            >
              {!isMobile && <div>{props.otherActions}</div>}
              <div className={isMobile ? ["d-flex flex-column w-100"].join(" ") : "d-flex align-items-center"}>
                {props.onClose && (
                  <Button
                    onClick={handleClose}
                    color={props.closeColor || "inherit"}
                    size={isMobile ? "medium" : "large"}
                    className={isMobile ? "w-100" : ""}
                  >
                    {props.closeTitle || t("actions.close")}
                  </Button>
                )}

                {props.onAccept && (
                  <Button
                    onClick={handleAccept}
                    disabled={props.acceptDisabled}
                    color={props.acceptColor || "primary"}
                    className={isMobile ? "mt-2 w-100" : "ml-3"}
                    variant="contained"
                    size={isMobile ? "medium" : "large"}
                  >
                    {props.acceptTitle || t("actions.accept")}
                  </Button>
                )}
              </div>
              {isMobile && props.otherActions && <div className={"w-100 mt-2"}>{props.otherActions}</div>}
            </div>
          </DialogActions>
        )}
      </Fragment>
    );
  };
  return (
    <div>
      {props.transition && (
        <Dialog
          className={[props.className, "dialog-modal"].join(" ")}
          fullScreen={fullScreen}
          fullWidth={true}
          maxWidth={fullScreen ? null : props.maxWidth || "sm"}
          open={open}
          onClose={!props.blockClose ? handleClose : null}
          TransitionComponent={Transition}
          disableEnforceFocus
          scroll={props.scroll || "paper"}
        >
          {getDialogContent()}
        </Dialog>
      )}
      {!props.transition && (
        <Dialog
          className={[props.className, "dialog-modal"].join(" ")}
          fullScreen={fullScreen}
          disableEnforceFocus
          fullWidth={true}
          maxWidth={fullScreen ? null : props.maxWidth || "sm"}
          open={open}
          onClose={!props.blockClose ? handleClose : null}
        >
          {getDialogContent()}
        </Dialog>
      )}
    </div>
  );
};

export default AlertDialog;
